/**
 * Employee Entry Card
 *
 * Card displaying a single employee with their calculated contributions.
 * Supports view, edit, and delete modes.
 */

import { useState } from 'react';
import {
  EmployeeCalculation,
  PAY_INDICATOR_LABELS,
  CATEGORY_LABELS,
} from '@/types/nib/manualEntry';
import { formatCurrency } from '@/services/utils/formatters';

interface EmployeeEntryCardProps {
  employee: EmployeeCalculation;
  isHospitality: boolean;
  onEdit: (employee: EmployeeCalculation) => void;
  onRemove: (employeeId: number) => void;
  isRemoving?: boolean;
}

export function EmployeeEntryCard({
  employee,
  isHospitality,
  onEdit,
  onRemove,
  isRemoving = false,
}: EmployeeEntryCardProps) {
  const [showDetails, setShowDetails] = useState(false);

  const hasWarnings = employee.warnings.length > 0;

  return (
    <div
      className={`
        bg-white rounded-lg border-2 p-4
        ${hasWarnings ? 'border-yellow-300' : 'border-gray-200'}
        ${isRemoving ? 'opacity-50' : ''}
      `}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-700 font-semibold text-sm">
              {employee.name.charAt(0)}
            </span>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{employee.name}</h3>
            <p className="text-sm text-gray-500">NI: {employee.ni_number}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(employee)}
            disabled={isRemoving}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            title="Edit"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <button
            onClick={() => onRemove(employee.id)}
            disabled={isRemoving}
            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            title="Remove"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Summary Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-3 border-t border-b border-gray-100">
        <div>
          <span className="text-xs text-gray-500 block">Basic Wages</span>
          <span className="font-medium">
            {formatCurrency(employee.basic_wages)}
          </span>
        </div>
        {isHospitality && (
          <div>
            <span className="text-xs text-gray-500 block">Gratuity</span>
            <span className="font-medium">
              {formatCurrency(employee.gratuity_wages)}
            </span>
          </div>
        )}
        <div>
          <span className="text-xs text-gray-500 block">Weeks</span>
          <span className="font-medium">{employee.weeks_worked}</span>
        </div>
        <div>
          <span className="text-xs text-gray-500 block">Pay</span>
          <span className="font-medium">
            {PAY_INDICATOR_LABELS[employee.pay_indicator]}
          </span>
        </div>
      </div>

      {/* Contribution Breakdown */}
      <div className="pt-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            EE ({CATEGORY_LABELS[employee.category].split(' ')[0]}):
          </span>
          <span className="font-medium">
            {formatCurrency(employee.employee_contribution)}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">ER:</span>
          <span className="font-medium">
            {formatCurrency(employee.employer_contribution)}
          </span>
        </div>
        {isHospitality && employee.gratuity_contribution > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Gratuity:</span>
            <span className="font-medium">
              {formatCurrency(employee.gratuity_contribution)}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between text-sm font-semibold border-t border-gray-200 pt-2 mt-2">
          <span>Total:</span>
          <span className="text-blue-600">
            {formatCurrency(employee.total_contribution)}
          </span>
        </div>
      </div>

      {/* Warnings */}
      {hasWarnings && (
        <div className="mt-3 p-2 bg-yellow-50 rounded-md">
          {employee.warnings.map((warning, i) => (
            <p
              key={i}
              className="text-xs text-yellow-700 flex items-start gap-1"
            >
              <svg
                className="w-4 h-4 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {warning}
            </p>
          ))}
        </div>
      )}

      {/* Expand Details */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="mt-3 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
      >
        <svg
          className={`w-3 h-3 transition-transform ${showDetails ? 'rotate-90' : ''}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clipRule="evenodd"
          />
        </svg>
        {showDetails ? 'Hide details' : 'Show details'}
      </button>

      {showDetails && (
        <div className="mt-2 p-3 bg-gray-50 rounded-md text-xs space-y-1">
          <p>
            <span className="text-gray-500">Category:</span>{' '}
            <span className="font-medium">
              {employee.category} - {CATEGORY_LABELS[employee.category]}
            </span>
          </p>
          <p>
            <span className="text-gray-500">Truncated Wages:</span>{' '}
            <span className="font-medium">
              {formatCurrency(employee.truncated_wages)}
            </span>
          </p>
          <p>
            <span className="text-gray-500">Capped Wages:</span>{' '}
            <span className="font-medium">
              {formatCurrency(employee.capped_wages)}
            </span>
            {employee.was_capped && (
              <span className="text-yellow-600 ml-1">
                (capped at {formatCurrency(employee.cap_applied || 0)})
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

export default EmployeeEntryCard;
